import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle, Play } from '@untitledui/icons';
import { Calendar } from '@untitledui/icons/Calendar';
import { Clock } from '@untitledui/icons/Clock';
import { InfoCircle } from '@untitledui/icons/InfoCircle';
import { LinkExternal01 } from '@untitledui/icons/LinkExternal01';
import { useMemo, useRef, useState } from 'react';

import type { PrequalificationResultDto } from '@/api/core-api/types.gen';

import {
  PrequalificationResult,
  ScreenerResponseStatus,
} from '@/api/core-api/types.gen';
import { AlertFloating } from '@/components/application/alerts/alerts';
import { Badge } from '@/components/base/badges/badges';
import { BaseBanner } from '@/components/base/banner/banner';
import { Button } from '@/components/base/buttons/button';
import { notify } from '@/components/base/notifications/notifications';
import { useCompleteProject } from '@/hooks/api/use-complete-project';
import { useResendPayoutEmail } from '@/hooks/api/use-resend-payout-email';
import { useStartUnmoderatedProject } from '@/hooks/api/use-start-unmoderated-project';
import { useBookingNavigation } from '@/hooks/use-booking-navigation';
import { useHydrated } from '@/hooks/use-hydrated';
import { useScreenCaptureSupport } from '@/hooks/use-screen-capture-support';
import {
  bannerEmojiBg,
  bannerEmojiCircle,
  bannerEmojiLgText,
} from '@/styles/banner-shared-styles';
import { cx } from '@/utils/cx';
import {
  formatDateTime,
  getDaysAgoText,
  getDaysFromNowText,
} from '@/utils/date';
import { openMeetingTarget } from '@/utils/meeting-target';
import { isModeratedStudy } from '@common/utils';

import type { TaskProgress } from '../../../-utils/task-progress';

import { resolveContactResearcherTarget } from '../../../-utils/contact-researcher-target';
import { CancelApplicationDialog } from './cancel-application-dialog';
import { PrequalificationBanner } from './prequalification-banner';

type ProjectDataProps = {
  screenerResponseStatus?: ScreenerResponseStatus;
  scheduledAt?: string;
  bookingUrl: string;
  meetingJoinTarget?: string;
  cancelBookingUrl: string;
  contactUrl: string;
  rescheduleUrl: string;
  profileEmail: string;
  requiresManualCompletion: boolean;
  projectCompletionId?: string;
  /** Set when the researcher invited this participant (stored invitation). */
  inviteId?: string | null;
};

export function StatusBanner({
  projectData,
  projectId,
  projectName,
  user,
  kindOfResearch,
  exposeParticipantEmails,
  taskProgress,
  isProjectClosed,
  isClosedToNewApplications,
  isStudyOver = false,
  isTargetReached = false,
  hasAlreadyApplied,
  prequalifies,
  requiresScreenShare = false,
  onVerifyWorkEmail,
}: {
  projectData: ProjectDataProps;
  /**
   * Where an invited participant is with the task, from `getTaskProgress` —
   * the same model the My Projects cards use.
   */
  taskProgress: TaskProgress | null;
  isProjectClosed: boolean;
  isClosedToNewApplications: boolean;
  /**
   * The study is finished (closed, or recruitment complete). The server
   * refuses both starting the task and redeeming a completion, so this is not
   * waived for someone who already applied or was invited — unlike
   * `isClosedToNewApplications`, which is about applying only.
   */
  isStudyOver?: boolean;
  /**
   * The server would refuse this participant's first start because the
   * project has delivered its target (PX-440). The Start button stays but is
   * disabled, with the reason on hover — unlike `isStudyOver`, the project is
   * still running, so it must not read as closed. Already false for someone
   * who has started, who may still return to the task.
   */
  isTargetReached?: boolean;
  hasAlreadyApplied: boolean;
  projectId: string;
  projectName: string;
  user?: { id: string } | null | undefined;
  kindOfResearch?: number;
  exposeParticipantEmails: boolean;
  prequalifies?: PrequalificationResultDto;
  /**
   * The interview records the participant's screen (server-derived from the
   * script). Screen capture is desktop-only, so on a device without it the
   * interview cannot be completed and must not be startable.
   */
  requiresScreenShare?: boolean;
  onVerifyWorkEmail?: () => void;
}) {
  const navigate = useNavigate();
  const openBooking = useBookingNavigation(projectId);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const resendPayoutEmailMutation = useResendPayoutEmail();
  const startUnmoderatedProjectMutation = useStartUnmoderatedProject();
  const completeProjectMutation = useCompleteProject();
  const screenCaptureSupported = useScreenCaptureSupport();
  // `undefined` (pre-hydration) is not "unsupported" — only block once the
  // device has actually answered, so desktop users never see a flash of it.
  const blockedByDevice =
    requiresScreenShare && screenCaptureSupported === false;
  const playIcon = useRef(<Play />).current;
  const calendarIcon = useRef(<Calendar />).current;
  const checkCircleIcon = useRef(<CheckCircle className="size-4" />).current;

  const {
    scheduledAt,
    bookingUrl,
    meetingJoinTarget,
    screenerResponseStatus,
    cancelBookingUrl,
    contactUrl,
    rescheduleUrl,
    profileEmail,
    requiresManualCompletion,
    projectCompletionId,
    inviteId,
  } = projectData;
  // Called before the early returns below; '' until hydrated, so the
  // server-rendered banner uses the undated copy.
  const hydrated = useHydrated();
  const startedAgo =
    hydrated && taskProgress?.stage === 'started'
      ? getDaysAgoText(taskProgress.startedAt)
      : '';
  const completedAgo =
    hydrated && taskProgress?.stage === 'complete'
      ? getDaysAgoText(taskProgress.completedAt)
      : '';
  const hasStarted = taskProgress?.stage === 'started';
  const userId = user?.id;
  const isAuthenticated = !!userId;

  // "Contact researcher" → native inbox: the existing thread, or the compose
  // flow preselected with the project when no conversation exists yet.
  const contactResearcher = () => {
    const target = resolveContactResearcherTarget(contactUrl, projectId);
    void navigate({
      to: '/participants/messages',
      search:
        target.kind === 'conversation'
          ? { conversation: target.conversationUid }
          : {
              projectId: target.projectId,
              surveyResponseId: target.surveyResponseId,
            },
    });
  };

  const dateText = useMemo(() => {
    if (!scheduledAt) return null;
    return {
      daysFromNow: getDaysFromNowText(scheduledAt),
      dateTime: formatDateTime(scheduledAt),
    };
  }, [scheduledAt]);

  // Status checks are ordered most → least advanced so a completed / paid
  // participant never falls through to invite/pending UI.
  if (screenerResponseStatus === ScreenerResponseStatus.PAID && userId) {
    return (
      <BaseBanner
        variant="success"
        className="mb-4"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.success,
              bannerEmojiLgText.sm,
            )}
          >
            💸
          </div>
        }
        title={<Trans>Payment sent!</Trans>}
        message={
          <Trans>
            Congratulations! We&apos;ve sent an email to{' '}
            <span className="font-semibold">{profileEmail}</span> with
            instructions on how to redeem your payment.
          </Trans>
        }
        action={
          <div className="mt-2 p-0">
            <Button
              color="link-color"
              isLoading={resendPayoutEmailMutation.isPending}
              onClick={() => {
                resendPayoutEmailMutation.mutate({ projectId, userId });
              }}
            >
              <Trans>Resend redemption email</Trans>
            </Button>
          </div>
        }
      />
    );
  }

  // `getTaskProgress` only reports `complete` while the response is still in
  // flight (INVITED/SCHEDULED): done, and awaiting the researcher.
  const isAwaitingApprovalAfterTaskComplete =
    taskProgress?.stage === 'complete';

  if (
    screenerResponseStatus === ScreenerResponseStatus.ATTENDED ||
    isAwaitingApprovalAfterTaskComplete
  ) {
    return (
      <BaseBanner
        className="mb-4"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.brand,
              bannerEmojiLgText.sm,
            )}
          >
            🎉
          </div>
        }
        title={
          // ATTENDED means the researcher has already confirmed; only the
          // still-INVITED completion is waiting on them.
          isAwaitingApprovalAfterTaskComplete ? (
            <Trans>Complete. Awaiting researcher confirmation</Trans>
          ) : (
            <Trans>Completed and awaiting approval</Trans>
          )
        }
        message={
          <>
            {isAwaitingApprovalAfterTaskComplete && completedAgo && (
              <span className="mb-1 block font-medium">
                <Trans>You completed this project {completedAgo}.</Trans>
              </span>
            )}
            <Trans>
              A researcher will review your submission and, once that&apos;s
              done, they&apos;ll update their side and process your payment if
              it applies. This review and payment process can take up to{' '}
              <span className="font-semibold">10 business days</span> after the
              project deadline, so if you don&apos;t hear back right away, no
              worries — it&apos;s still moving along.
            </Trans>
          </>
        }
        action={
          <div className="mt-2 p-0">
            <Button color="link-color" onClick={() => contactResearcher()}>
              <Trans>Contact researcher</Trans>
            </Button>
          </div>
        }
      />
    );
  }

  // `isStudyOver` is checked without the already-applied exemption on purpose:
  // once recruitment is complete the server refuses the start, so an invitee
  // must lose the button rather than click it into an error toast (PX-410).
  if (
    isProjectClosed ||
    isStudyOver ||
    (isClosedToNewApplications && !hasAlreadyApplied)
  ) {
    return (
      <BaseBanner
        variant="warning"
        className="mb-4 shadow-xs"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.warning,
              bannerEmojiLgText.md,
            )}
          >
            🚧
          </div>
        }
        title={<Trans>Project closed</Trans>}
        message={
          <Trans>
            We&apos;re sorry, but this project is not accepting new applications
            at this time. Don&apos;t worry, there are plenty of other projects
            that might be perfect for you.
          </Trans>
        }
        action={
          <div className="mt-2">
            <Button onClick={() => navigate({ to: '/participants/projects' })}>
              <Trans>View more projects</Trans>
            </Button>
          </div>
        }
      />
    );
  }

  if (!userId) {
    return null;
  }

  if (screenerResponseStatus === ScreenerResponseStatus.SCHEDULED) {
    const scheduledBadge = dateText ? (
      <Badge
        className="w-fit border-utility-purple-100 bg-utility-purple-100 font-semibold text-utility-purple-700"
        size="sm"
      >
        <Clock className="mr-1 size-3 text-utility-purple-700" />
        <Trans>
          {dateText.daysFromNow} - {dateText.dateTime}
        </Trans>
      </Badge>
    ) : null;

    return (
      <BaseBanner
        className="mb-4"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.brand,
              bannerEmojiLgText.md,
            )}
          >
            🚀
          </div>
        }
        title={
          <span className="flex w-full items-center justify-between">
            <Trans>Your session is scheduled</Trans>
            {scheduledBadge && (
              <span className="hidden md:inline-flex">{scheduledBadge}</span>
            )}
          </span>
        }
        message={
          <>
            <Trans>
              You&apos;ve successfully reserved your session. Please be ready to
              share your insights at the scheduled time.
            </Trans>
            {scheduledBadge && (
              <span className="mt-2 block md:hidden">{scheduledBadge}</span>
            )}
          </>
        }
        action={
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
            {meetingJoinTarget && (
              <Button
                color="link-color"
                iconLeading={LinkExternal01}
                onClick={() => openMeetingTarget(meetingJoinTarget)}
              >
                <Trans>Join</Trans>
              </Button>
            )}
            <Button
              color="link-color"
              onClick={() => openBooking(cancelBookingUrl)}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              color="link-color"
              onClick={() => openBooking(rescheduleUrl)}
            >
              <Trans>Reschedule</Trans>
            </Button>
          </div>
        }
      />
    );
  }

  if (
    screenerResponseStatus === ScreenerResponseStatus.INVITED &&
    kindOfResearch
  ) {
    // AI-moderated interview: navigate to on-platform interview
    if (kindOfResearch === 12) {
      return (
        <>
          {blockedByDevice && (
            <AlertFloating
              className="mb-4"
              color="error"
              title={t`Desktop browser required`}
              description={t`This study requires you to share your screen which is currently not supported on mobile. Please attend this interview when you are at a computer.`}
            />
          )}
          <BaseBanner
            className="mb-4"
            icon={
              <div
                className={cx(
                  bannerEmojiCircle,
                  bannerEmojiBg.brand,
                  bannerEmojiLgText.md,
                )}
              >
                👋
              </div>
            }
            title={
              !hasStarted ? (
                <Trans>You&apos;ve been invited!</Trans>
              ) : startedAgo ? (
                <Trans>You started this interview {startedAgo}</Trans>
              ) : (
                <Trans>You&apos;ve started this interview</Trans>
              )
            }
            message={
              hasStarted ? (
                <Trans>
                  Return to the interview whenever you&apos;re ready.
                </Trans>
              ) : (
                <Trans>
                  The researcher invited you to participate in this interview.
                </Trans>
              )
            }
            action={
              <div className="mt-2 p-0">
                <Button
                  color="primary"
                  iconLeading={playIcon}
                  isDisabled={blockedByDevice}
                  onClick={() =>
                    void navigate({
                      to: '/participants/projects/$projectId/interview',
                      params: { projectId },
                    })
                  }
                >
                  <Trans>Go to interview</Trans>
                </Button>
              </div>
            }
          />
        </>
      );
    }

    if (!isModeratedStudy({ project: { kindOfResearch } })) {
      return (
        <BaseBanner
          className="mb-4"
          icon={
            <div
              className={cx(
                bannerEmojiCircle,
                bannerEmojiBg.brand,
                bannerEmojiLgText.md,
              )}
            >
              👋
            </div>
          }
          title={
            !hasStarted ? (
              <Trans>You&apos;ve been invited!</Trans>
            ) : startedAgo ? (
              <Trans>You started this project {startedAgo}</Trans>
            ) : (
              <Trans>You&apos;ve started this project</Trans>
            )
          }
          message={
            !hasStarted ? (
              <Trans>
                You&apos;re eligible to participate in this project. Everything
                is ready and you can start the project now whenever you&apos;re
                ready.
              </Trans>
            ) : requiresManualCompletion && projectCompletionId ? (
              <Trans>
                Pick up where you left off. Once you&apos;ve finished, select
                &ldquo;Complete project&rdquo; so the researcher can confirm it.
              </Trans>
            ) : (
              <Trans>
                Pick up where you left off whenever you&apos;re ready.
              </Trans>
            )
          }
          action={
            <div className="mt-2 flex gap-3 p-0">
              <div className="flex flex-row gap-2">
                <Button
                  color="primary"
                  isLoading={startUnmoderatedProjectMutation.isPending}
                  isDisabled={isTargetReached}
                  tooltip={
                    isTargetReached
                      ? t`This project has reached its participant limit.`
                      : undefined
                  }
                  iconLeading={playIcon}
                  onClick={() => {
                    startUnmoderatedProjectMutation.mutate(
                      { projectId, userId },
                      {
                        onSuccess: ({ surveyUrl }) => {
                          globalThis.location.href = surveyUrl;
                        },
                        onError: (error) => {
                          notify({
                            title: t`Failed to start project`,
                            description: error.message,
                            color: 'error',
                          });
                        },
                      },
                    );
                  }}
                >
                  {hasStarted ? (
                    <Trans>Go to project</Trans>
                  ) : (
                    <Trans>Start project</Trans>
                  )}
                </Button>
                {requiresManualCompletion && projectCompletionId && (
                  <Button
                    color="success"
                    size="md"
                    iconLeading={checkCircleIcon}
                    onClick={() =>
                      completeProjectMutation.mutate(
                        {
                          projectCompletionId,
                          userId,
                        },
                        {
                          onSuccess: () => {
                            notify({
                              title: t`Project completed, researcher will be notified.`,
                              color: 'success',
                            });
                          },
                          onError: (error) => {
                            notify({
                              title: t`Failed to complete project`,
                              description: error.message,
                              color: 'error',
                            });
                          },
                        },
                      )
                    }
                    isLoading={completeProjectMutation.isPending}
                  >
                    <Trans>Complete project</Trans>
                  </Button>
                )}
              </div>
              {/* Consent for starting. Once started, the researcher already
                  has the email (core-api exposes it on `projectStarted`), and
                  the button reads "Go to project", so the line is dropped. */}
              {exposeParticipantEmails && !hasStarted && (
                <p className="flex items-center gap-1 text-sm text-warning-primary">
                  <InfoCircle className="size-3.5 shrink-0 text-warning-primary" />
                  <Trans>
                    By clicking &ldquo;Start project,&rdquo; you agree to share
                    your email address with the researcher for this project.
                  </Trans>
                </p>
              )}
            </div>
          }
        />
      );
    }

    return (
      <BaseBanner
        className="mb-4"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.brand,
              bannerEmojiLgText.md,
            )}
          >
            👋
          </div>
        }
        title={<Trans>You&apos;ve been invited!</Trans>}
        message={
          <Trans>
            You&apos;re eligible to participate in this project. Please pick a
            time that works for you from the available schedule.
          </Trans>
        }
        action={
          <div className="mt-2 p-0">
            <Button
              color="primary"
              iconLeading={calendarIcon}
              onClick={() => openBooking(bookingUrl)}
            >
              <Trans>Book your spot</Trans>
            </Button>
          </div>
        }
      />
    );
  }

  // External-screener: the application is with the partner's follow-up
  // screener — the apply page owns the actual handoff (and the loop-safe
  // manual continue), so this banner just routes there.
  if (
    screenerResponseStatus ===
    ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS
  ) {
    return (
      <BaseBanner
        className="mb-4"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.brand,
              bannerEmojiLgText.md,
            )}
          >
            📝
          </div>
        }
        title={<Trans>One more step to finish applying</Trans>}
        message={
          <Trans>
            This study includes a short follow-up screener run by the research
            team. Complete it to submit your application.
          </Trans>
        }
        action={
          <div className="mt-2 p-0">
            <Button
              color="primary"
              onClick={() => {
                void navigate({
                  to: '/participants/projects/$projectId/apply',
                  params: { projectId },
                });
              }}
            >
              <Trans>Continue application</Trans>
            </Button>
          </div>
        }
      />
    );
  }

  if (screenerResponseStatus === ScreenerResponseStatus.PENDING) {
    return (
      <BaseBanner
        className="mb-4"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.brand,
              bannerEmojiLgText.md,
              'transform-[scale(-1,1)]',
            )}
          >
            📣
          </div>
        }
        title={<Trans>Application has been submitted!</Trans>}
        message={
          <Trans>
            We&apos;ve let the researchers know you&apos;ve applied.
            They&apos;ll review everyone&apos;s applications and choose
            participants. If you&apos;re selected, we&apos;ll be in touch with
            next steps so you can get started.
          </Trans>
        }
        action={
          userId ? (
            <div className="mt-2">
              <Button
                color="link-color"
                onClick={() => setIsConfirmingCancel(true)}
              >
                <Trans>Cancel application</Trans>
              </Button>
              <CancelApplicationDialog
                isOpen={isConfirmingCancel}
                onClose={() => setIsConfirmingCancel(false)}
                projectId={projectId}
                userId={userId}
                projectName={projectName}
                status={screenerResponseStatus}
                kindOfResearch={kindOfResearch}
              />
            </div>
          ) : undefined
        }
      />
    );
  }

  if (screenerResponseStatus === ScreenerResponseStatus.CANCELLED) {
    return null;
  }

  // Invited to apply — a researcher's invitation (Participant Database or a
  // program follow-up) with no application yet. Distinct from the INVITED
  // response status above, which is "selected after applying": here the ask
  // is to apply at all. Whether the screener is waived is said by the label
  // beside Apply, not here.
  if (isAuthenticated && !hasAlreadyApplied && inviteId) {
    return (
      <BaseBanner
        variant="brand"
        className="mb-4 shadow-xs"
        icon={
          <div
            className={cx(
              bannerEmojiCircle,
              bannerEmojiBg.brand,
              bannerEmojiLgText.md,
            )}
          >
            👋
          </div>
        }
        title={<Trans>This researcher invited you</Trans>}
        message={
          <Trans>
            You&apos;ve taken part in research with this team before, and
            they&apos;d like you in this study too. Apply to let them know
            you&apos;re interested.
          </Trans>
        }
      />
    );
  }

  // Show eligibility banner when no other status banner applies
  if (
    isAuthenticated &&
    prequalifies?.result === PrequalificationResult.DISQUALIFIED
  ) {
    return (
      <div className="mb-4">
        <PrequalificationBanner
          prequalifies={prequalifies}
          onVerifyWorkEmail={onVerifyWorkEmail}
        />
      </div>
    );
  }

  return null;
}
